import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { usePlots } from "@/hooks/useFirestore";
import {
  useUpdateExperiment,
  useUpdatePlotMetadataFromCSV,
} from "@/services/dataService";
import { Experiment, UpdateExperimentInput } from "@/types/experiment";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import EditExperimentForm from "./EditExperimentForm";

interface EditExperimentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  experiment: Experiment | null;
  trialId: string;
  onExperimentUpdated: () => void;
  onExperimentDeleted: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

const EditExperimentDialog: React.FC<EditExperimentDialogProps> = ({
  open,
  onOpenChange,
  experiment,
  trialId,
  onExperimentUpdated,
  onExperimentDeleted,
  canEdit = false,
  canDelete = false,
}) => {
  const { toast } = useToast();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState<{ progress: number; message: string } | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  // Warn before discarding in-progress experiment edits (Escape, click-outside, X, Cancel).
  const guard = useUnsavedChangesGuard({ isDirty, onOpenChange });

  const updateExperiment = useUpdateExperiment();
  const updatePlotMetadataFromCSV = useUpdatePlotMetadataFromCSV();
  const { plots } = usePlots(trialId, experiment?.id);

  const handleSubmit = async (data: UpdateExperimentInput) => {
    if (!experiment?.id || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await updateExperiment(trialId, experiment.id, data);
      toast({
        title: "Experiment updated",
        description: `${data.name} has been updated`,
      });
      onExperimentUpdated();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update experiment",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    // Don't manually close the AlertDialog (setShowDeleteConfirm) — let Radix's
    // AlertDialogAction handle it via onOpenChange to avoid stale pointer-events on body.
    // Close the parent edit dialog and trigger the deletion callback.
    onOpenChange(false);
    onExperimentDeleted();
  };

  const handleMetadataUpdate = async (parsedData: Record<string, string>[]) => {
    if (!experiment?.id || isSubmitting) return;

    setIsSubmitting(true);
    setSubmissionProgress({ progress: 0, message: "Preparing metadata update..." });

    try {
      const plotMapConfig = {
        plotIDName: experiment.plotMap?.plotIDName || "Plot ID",
        rowIDName: experiment.plotMap?.rowIDName,
        colIDName: experiment.plotMap?.colIDName,
      };

      const result = await updatePlotMetadataFromCSV(
        trialId,
        experiment.id,
        parsedData,
        plotMapConfig,
        (progress, message) => {
          setSubmissionProgress({ progress, message });
        }
      );

      if (result.success) {
        toast({
          title: "Metadata updated",
          description: `Successfully updated metadata for ${result.updatedCount} plots`,
        });
        onExperimentUpdated();
        onOpenChange(false);
      } else {
        throw new Error(result.errors.join(", ") || "Failed to update metadata");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update plot metadata",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setSubmissionProgress(null);
    }
  };

  if (!experiment) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={guard.guardedOnOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Experiment</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-4 -mr-2">
            <EditExperimentForm
              experiment={experiment}
              plots={plots}
              onSubmit={handleSubmit}
              onCancel={guard.requestClose}
              onDelete={handleDeleteClick}
              onMetadataUpdate={handleMetadataUpdate}
              showDelete={canDelete}
              onDirtyChange={setIsDirty}
            />

            {/* Progress indicator when submitting */}
            {isSubmitting && submissionProgress && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{submissionProgress.message}</span>
                  <span className="font-medium">{Math.round(submissionProgress.progress * 100)}%</span>
                </div>
                <Progress value={submissionProgress.progress * 100} className="h-2" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Experiment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{experiment?.name}"? This will permanently delete all
              tasks, plots, and data associated with this experiment. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {guard.confirmation}
    </>
  );
};

export default EditExperimentDialog;
